import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { sr, enUS, zhCN } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import HelpIcon from './HelpIcon';
import { getHelpContent } from '../utils/helpContent.js';

export default function Sidebar({ 
  uploadedItems, 
  loadingFiles, 
  loadingMore, 
  hasMore, 
  onLoadMore, 
  fileInputRef, 
  handleFileChange, 
  handleUploadClick, 
  handleFileClick, 
  handleFileDownload,
  handleFileDelete,
  sidebarOpen, 
  selectedFileId,
  sortBy,
  sortOrder,
  onSortChange,
  filterBy,
  onFilterChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  companyMembers,
}) {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const listRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { item: {...}, position: {x, y} }
  
  // Get date-fns locale based on current language
  const getDateLocale = () => {
    const lang = i18n.language || 'en';
    if (lang === 'sr') return sr;
    if (lang === 'zh') return zhCN;
    return enUS;
  };
  
  // Get locale string for date formatting
  const getDateLocaleString = () => {
    const lang = i18n.language || 'en';
    if (lang === 'sr') return 'sr-RS';
    if (lang === 'zh') return 'zh-CN';
    return 'en-US';
  };

  // Convert date strings to Date objects for DatePicker (avoid timezone issues)
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Convert Date object to YYYY-MM-DD string (avoid timezone issues)
  const formatDateToString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateFromValue = parseDateString(dateFrom);
  const dateToValue = parseDateString(dateTo);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement || !onLoadMore || !hasMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = listElement;
      // Load more when user is 100px from bottom
      if (scrollHeight - scrollTop - clientHeight < 100 && !loadingMore) {
        onLoadMore();
      }
    };

    listElement.addEventListener('scroll', handleScroll);
    return () => listElement.removeEventListener('scroll', handleScroll);
  }, [onLoadMore, hasMore, loadingMore]);

  return (
    <aside style={{
      width: 320,
      background: colors.card,
      borderRight: `1px solid ${colors.border}`,
      boxShadow: `0 0 16px ${colors.shadow}`,
      padding: '16px 20px 20px 20px',
      display: sidebarOpen ? 'flex' : 'none',
      flexDirection: 'column',
      alignItems: 'stretch',
      transition: 'all 0.3s',
      position: 'relative',
    }}>
      <input
        type="file"
        accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      <div style={{ position: 'relative', marginBottom: 28, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          style={{
            background: colors.primary,
            color: 'white',
            fontWeight: 700,
            fontSize: 16,
            borderRadius: 8,
            padding: '12px 0',
            border: 'none',
            boxShadow: `0 2px 8px ${colors.shadow}`,
            cursor: 'pointer',
            flex: 1,
            transition: 'background-color 0.2s ease',
          }}
          onClick={handleUploadClick}
          onMouseEnter={(e) => e.target.style.background = colors.primaryHover}
          onMouseLeave={(e) => e.target.style.background = colors.primary}
        >
          + {t('sidebar.uploadButton')}
        </button>
        <div style={{ pointerEvents: 'auto' }}>
          <HelpIcon
            content={getHelpContent('sidebar.upload')?.short}
            detailedContent={getHelpContent('sidebar.upload')?.detailed}
            position="bottom"
            size={16}
          />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{fontWeight: 700, color: colors.text, fontSize: 17, display: 'flex', alignItems: 'center'}}>
          {t('sidebar.yourFiles')}
          <HelpIcon
            content={getHelpContent('sidebar.fileActions')?.short}
            detailedContent={getHelpContent('sidebar.fileActions')?.detailed}
            position="bottom"
            size={16}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <HelpIcon
            content={getHelpContent('sidebar.filters')?.short}
            detailedContent={getHelpContent('sidebar.filters')?.detailed}
            position="left"
            size={14}
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              background: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 12,
              color: colors.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'border-color 0.2s, color 0.2s',
            }}
            title={showFilters ? t('sidebar.hideFilters') : t('sidebar.showFilters')}
          >
            <i className={`fas fa-${showFilters ? 'chevron-up' : 'chevron-down'}`}></i>
            <span>{showFilters ? t('sidebar.hide') : t('sidebar.filters')}</span>
          </button>
        </div>
      </div>
      
      {/* Sort and Filter Controls */}
      {showFilters && (
      <>
      {/* Sort Controls */}
      <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 6 }}>{t('sort.sortBy')}:</div>
        <select
          value={sortBy}
          onChange={(e) => {
            const newSortBy = e.target.value;
            onSortChange(newSortBy, sortOrder);
          }}
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
            fontSize: 13,
            backgroundColor: colors.background,
            color: colors.text,
            marginBottom: 6,
            cursor: 'pointer',
            transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
          }}
        >
          <option value="time">{t('sort.time')}</option>
          <option value="uploader">{t('sort.uploader')}</option>
          <option value="originalName">{t('sort.fileName')}</option>
        </select>
        <button
          onClick={() => onSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')}
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
            fontSize: 12,
            backgroundColor: colors.backgroundLight,
            color: colors.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
          }}
        >
          <i className={`fas fa-sort-${sortOrder === 'asc' ? 'amount-up' : 'amount-down'}`}></i>
          {sortOrder === 'asc' ? t('sort.ascending') : t('sort.descending')}
        </button>
      </div>

      {/* Filter Controls */}
      <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 6 }}>{t('sidebar.filter')}</div>
        <select
          value={filterBy}
          onChange={(e) => onFilterChange(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: 6,
            border: `1px solid ${colors.border}`,
            fontSize: 13,
            backgroundColor: colors.background,
            color: colors.text,
            marginBottom: 8,
            cursor: 'pointer',
            transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
          }}
        >
          <option value="me">{t('sidebar.myFiles')}</option>
        </select>
        <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 6 }}>{t('sidebar.period')}</div>
        <div style={{ marginBottom: 6 }}>
          <DatePicker
            selected={dateFromValue}
            onChange={(date) => {
              const dateStr = formatDateToString(date);
              onDateFromChange(dateStr);
            }}
            selectsStart
            startDate={dateFromValue}
            endDate={dateToValue}
            placeholderText={t('sidebar.dateFrom')}
            locale={getDateLocale()}
            dateFormat="dd.MM.yyyy"
            isClearable
          />
        </div>
        <div>
          <DatePicker
            selected={dateToValue}
            onChange={(date) => {
              const dateStr = formatDateToString(date);
              onDateToChange(dateStr);
            }}
            selectsEnd
            startDate={dateFromValue}
            endDate={dateToValue}
            minDate={dateFromValue}
            placeholderText={t('sidebar.dateTo')}
            locale={getDateLocale()}
            dateFormat="dd.MM.yyyy"
            isClearable
          />
        </div>
      </div>
      </>
      )}

      <ul ref={listRef} style={{listStyle: 'none', padding: 0, margin: 0, flex: 1, overflowY: 'auto'}}>
        {loadingFiles ? (
          <li style={{color: colors.textLight, fontStyle: 'italic', padding: '10px 0'}}>{t('common.loading')}</li>
        ) : uploadedItems.length === 0 ? (
          <li style={{color: colors.textLight, fontStyle: 'italic', padding: '10px 0'}}>{t('file.noFiles')}</li>
        ) : (
          <>
          {uploadedItems.map(item => (
            <li key={item.id} style={{
              padding: '10px 12px',
              borderBottom: `1px solid ${colors.borderLight}`,
              color: selectedFileId === item.id ? '#fff' : colors.primary,
              background: selectedFileId === item.id ? colors.primary : 'transparent',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background 0.15s',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: selectedFileId === item.id ? 6 : 0,
              position: 'relative',
            }}
            onClick={() => handleFileClick(item)}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.originalName}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileDownload(e, item);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: selectedFileId === item.id ? '#fff' : colors.textSecondary,
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 16,
                    }}
                    title={t('sidebar.downloadFile')}
                    onMouseEnter={(e) => {
                      e.target.style.background = selectedFileId === item.id ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    <i className="fas fa-download"></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setDeleteConfirm({
                        item,
                        position: {
                          x: rect.right - 220, // Position confirmation bubble to the left of button
                          y: rect.top - 90, // Position above button to avoid scrolling issues
                        }
                      });
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: selectedFileId === item.id ? '#fff' : colors.error,
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 16,
                    }}
                    title={t('sidebar.deleteFile')}
                    onMouseEnter={(e) => {
                      e.target.style.background = selectedFileId === item.id ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <span style={{fontSize: 13, color: selectedFileId === item.id ? 'rgba(255,255,255,0.8)' : colors.textSecondary, fontWeight: 400, marginTop: 4}}>
                {item.uploaderEmail || item.uploader} &middot; {new Date(item.updatedAt).toLocaleString(getDateLocaleString())}
              </span>
            </li>
          ))}
          {loadingMore && (
            <li style={{color: colors.textLight, fontStyle: 'italic', padding: '10px 0', textAlign: 'center'}}>
              {t('dashboard.loadingMore')}
            </li>
          )}
          {!hasMore && uploadedItems.length > 0 && (
            <li style={{color: colors.textLight, fontStyle: 'italic', padding: '10px 0', textAlign: 'center', fontSize: 13}}>
              {t('sidebar.allFilesLoaded')}
            </li>
          )}
          </>
        )}
      </ul>

      {/* Delete Confirmation Bubble */}
      {deleteConfirm && (
        <>
          {/* Backdrop to close on click outside */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              background: 'transparent',
            }}
            onClick={() => setDeleteConfirm(null)}
          />
          {/* Confirmation bubble */}
          <div
            style={{
              position: 'fixed',
              left: `${deleteConfirm.position.x}px`,
              top: `${deleteConfirm.position.y}px`,
              background: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: '12px 16px',
              boxShadow: `0 4px 12px ${colors.shadowHover}`,
              zIndex: 1001,
              minWidth: 200,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 8 }}>
              {t('sidebar.deleteFileConfirm')}
            </div>
            <div style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 12 }}>
              {deleteConfirm.item.originalName}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '6px 12px',
                  background: colors.backgroundHover,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.text,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.background = colors.border}
                onMouseLeave={(e) => e.target.style.background = colors.backgroundHover}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  handleFileDelete(deleteConfirm.item);
                  setDeleteConfirm(null);
                }}
                style={{
                  padding: '6px 12px',
                  background: colors.error,
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </>
      )}
    </aside>
  );
} 